import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { map } from "rxjs";
import { NotificationHubFacade } from "@pcp/streaming";
import { AuthServiceFacade } from "@pcp/auth";
import { stopLessonActions } from "../../public/store/lesson.actions";
import { classroomLifecycleEvents } from "../../public/store/classroom-sync.actions";

/**
 * Emits the canonical `classroomLifecycleEvents.lessonHasBeenStopped` fact.
 *
 * Every dispatcher of that event lives in this file, so the classroom umbrella
 * owns its own lifecycle: the reducer stop-reset and
 * `LessonYjsLifecycleEffects.returnToDraftOnLessonStop$` react to the one event.
 *
 * The event is **teacher-scoped by construction**: both sources below are
 * teacher-only, so it never fires on a beamer or student. Consumers learn of a
 * stop through the SignalR `STOP_LESSON` command
 * (`classroomReceiverActions.receivedStopLessonCommand`) and react locally per
 * app — there is no synced "stopped" state and no cross-client stop event.
 */
@Injectable()
export class ClassroomLifecycleEffects {
    private readonly actions$ = inject(Actions);
    private readonly notificationHub = inject(NotificationHubFacade);
    private readonly auth = inject(AuthServiceFacade);

    /**
     * This teacher client's local stop chain ran to completion. Says nothing
     * about the beamer: the window close in that chain is best-effort and its
     * outcome is deliberately never verified.
     */
    readonly lessonStoppedLocally$ = createEffect(() => this.actions$.pipe(
        ofType(stopLessonActions.stopLesson_disconnectedFromClassroom),
        map(() => classroomLifecycleEvents.lessonHasBeenStopped()),
    ));

    /**
     * Received SignalR notification: the lesson has been stopped by another
     * teacher client (same account, different browser/device).
     *
     * The hub currently sends `NotifyLessonHasBeenStopped` to the teacher
     * group only, but this effect is registered in every app — the gate makes
     * the teacher-only requirement explicit client-side instead of relying on
     * server routing (a dormant API-side sender broadcasts to ALL groups).
     */
    readonly lessonStoppedByOtherTeacherClient$ = createEffect(() => this.notificationHub.onNotifyLessonHasBeenStopped$.pipe(
        this.auth.gateByClientType('Teacher'),
        map(() => classroomLifecycleEvents.lessonHasBeenStopped()),
    ));
}
